import { Box, Button, Card, CardActions, CardContent, CardHeader, FormHelperText, Stack, Tab, Tooltip, Typography, styled } from "@mui/material";
import { MprcFormSubHeader, MprcLink, MprcRequestErrorDialog, MprcSpinnerIndicator } from "components";
import { MprcChipTabs } from 'components/MprcChipTabs';
import { AwardResumeResponse, EvolutionPermissions } from "generated/CoreApi_v1";
import { useCoreApi } from "providers/CoreApiProvider";
import { useSession } from "providers/SessionProvider";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { DashboardPaths } from "routes/DashboardRouter";
import { AwardPointsCard } from 'views/award/components/AwardPointsCard';
import { AwardRankingCard } from 'views/award/components/AwardRankingCard';
import { BadgeInfoDialog } from "views/award/components/BadgeInfoDialog";

/////////////////////////////////////////////////////////////////////////////////////////////////

const CustomCard = styled(Card)(
    ({ theme }) => ({
        "&.MuiCard-root": {
            padding: theme.spacing(1),
        }
    })
);

const CustomCardHeader = styled(CardHeader)(
    ({ theme }) => ({
        "&.MuiCardHeader-root": {
            paddingLeft: 0,
            paddingRight: 13,
            paddingTop: 0,
            paddingBottom: 15
        },
        "&.MuiCardHeader-action": {
            marginTop: 0,
            marginRight: 0,
            alignSelf: "center"
        },
    })
);

const CustomCardContent = styled(CardContent)(
    ({ theme }) => ({
        "&.MuiCardContent-root": {
            padding: 8,
            "&:last-child": {
                paddingBottom: 0
            }
        },
    })
);

/////////////////////////////////////////////////////////////////////////////////////////////////


type PeriodType = "month" | "all";

interface IProps {
    onCloseMenu: () => void;
}

export const AwardResume: React.FC<IProps> = ({ onCloseMenu }) => {

    const { t } = useTranslation("award");
    const api = useCoreApi();
    const session = useSession();
    const navigate = useNavigate();

    const [dialog, setDialog] = useState<any>();
    const [loading, setLoading] = useState<boolean>(true);
    const [viewData, setViewData] = useState<AwardResumeResponse>();
    const [period, setPeriod] = useState<PeriodType>("month");
    const [viewError, setViewError] = useState<any>();

    ///
    const handleViewBadge = (badgeId: string) => {
        setDialog(
            <BadgeInfoDialog
                badgeId={badgeId}
                userId={session.user.userId}
                onClose={() => setDialog(null)}
            />
        );
    };

    const navigateToAwardDashboard = () => {
        navigate(DashboardPaths.UserEvolution, { state: { tab: EvolutionPermissions.Award } });
    };

    useEffect(() => {
        async function getData() {
            try {
                var result = await api.v1.award.getAwardResume({ currentMonth: period == "month" });
                setViewData(result.data);
            } catch (e) {
                setViewError(e);
            } finally {
                setLoading(false);
            }
        }

        if (loading) getData();

    }, [loading]);

    useEffect(() => {
        setLoading(true);
    }, [period]);

    ///
    return (
        <Box sx={{ width: 550, position: "relative" }}>
            {dialog}
            <MprcRequestErrorDialog error={viewError} onClose={() => setViewError(null)} />
            <CustomCard>
                <CustomCardHeader
                    action={
                        <MprcChipTabs
                            value={period}
                            onChange={(e, value) => setPeriod(value)}
                        >
                            <Tab
                                label={t("Header.MonthText")}
                                disabled={loading}
                                value={"month"}
                            />
                            <Tab
                                label={t("Header.AllText")}
                                disabled={loading}
                                value={"all"}
                            />
                        </MprcChipTabs>
                    }
                    title={t("Header.Title")}
                    subheader={<FormHelperText>{viewData?.rankingName}</FormHelperText>}
                >
                </CustomCardHeader>
                <>
                    {
                        loading
                            ?
                            <MprcSpinnerIndicator />
                            :
                            <>
                                <CustomCardContent>
                                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                                        {
                                            viewData && viewData.ranking > 0 &&
                                            <AwardRankingCard
                                                ranking={viewData.ranking}
                                                size="small"
                                                subtitle={t(period == "month" ? "Header.RankingMonthText" : "Header.RankingHistoryText")}
                                            />
                                        }
                                        <AwardPointsCard
                                            points={viewData?.points ?? 0}
                                            size="small"
                                            subtitle={t(period == "month" ? "Header.PointsMonthText" : "Header.PointsHistoryText")}
                                        />
                                    </Stack>
                                    <Stack marginTop={6}>
                                        <MprcFormSubHeader title={t(viewData && viewData.badges.length > 0 ? "Header.LastBadgesText" : "Header.NoBadgeText")} />
                                        {
                                            viewData && viewData.badges.length == 0 &&
                                            <MprcLink onClick={navigateToAwardDashboard}>
                                                <Typography variant="captionHeader">
                                                    {t("Header.NoBadgeMessageText")}
                                                </Typography>
                                            </MprcLink>
                                        }
                                    </Stack>
                                    {
                                        viewData && viewData.badges.length > 0 &&
                                        <Stack direction="row" justifyContent="flex-start" alignContent="flex-start" flexWrap="wrap" spacing={3} marginTop={2}>
                                            {
                                                viewData.badges.map((item) => (
                                                    <MprcLink onClick={() => handleViewBadge(item.badgeId)}>
                                                        <Tooltip title={item.name}>
                                                            <img
                                                                style={{ width: 48, height: 64, objectFit: 'scale-down' }}
                                                                src={item.thumbnailUrl}
                                                            />
                                                        </Tooltip>
                                                    </MprcLink>
                                                ))
                                            }
                                        </Stack>
                                    }
                                </CustomCardContent>

                                <CardActions sx={{ justifyContent: "center" }}>
                                    <Button variant="contained" fullWidth size="small" onClick={navigateToAwardDashboard}>{t("Header.ReportkLinkButtonText")}</Button>
                                </CardActions>
                            </>
                    }
                </>
            </CustomCard>
        </Box >
    );
};
